import { useState } from "react";
import { api } from "@/api/client";

export function AuthPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await api.magicLink(email.trim());
      setSent(true);
      if (res.message) setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send sign-in link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-md">
      <h1 className="font-serif text-heading text-primary">Sign in to upload</h1>
      <p className="mt-3 text-body text-secondary">
        We&apos;ll email you a one-time link. No password required.
      </p>

      {sent ? (
        <div
          className="mt-8 rounded-lg border border-ink/10 bg-cream p-6 shadow-card"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start gap-3">
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-success/10 text-success"
              aria-hidden="true"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </span>
            <div>
              <p className="font-medium text-primary">Check your inbox</p>
              <p className="mt-2 text-body-sm text-secondary">
                If <strong>{email}</strong> is valid, we sent a sign-in link. It expires in 15 minutes.
              </p>
              <button
                type="button"
                className="mt-4 text-body-sm text-tertiary underline transition-colors duration-fast hover:text-primary"
                onClick={() => setSent(false)}
              >
                Use a different email
              </button>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4" aria-busy={loading}>
          <label className="block">
            <span className="font-mono text-label uppercase text-tertiary">Work email</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              className="mt-1.5 w-full rounded-md border border-ink/15 bg-cream px-3 py-2.5 text-body text-primary shadow-card transition-colors duration-fast focus:border-coral disabled:opacity-60"
              placeholder="you@company.com"
            />
          </label>
          {error && (
            <p className="rounded-md bg-error/10 px-3 py-2 text-body-sm text-error" role="alert">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-md bg-coral px-4 py-3 font-mono text-button text-surface transition-opacity duration-fast hover:opacity-90 disabled:opacity-50"
          >
            {loading ? (
              <span className="inline-flex items-center justify-center gap-2">
                <svg
                  className="h-4 w-4 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Sending…
              </span>
            ) : (
              "Email me a sign-in link"
            )}
          </button>
        </form>
      )}
    </div>
  );
}
